/**
 * ADXTable - ADX/ATR Volatility Analysis Display
 *
 * Simple text-based table showing ADX trend strength and ATR volatility.
 * Provides clear trend quality indicators for traders.
 */

import React, { memo } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

/**
 * Table row component - Institutional Grade Styling
 */
const TableRow = ({ label, value, valueColor = '#fff', subValue = null }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 12px',
            borderBottom: '1px solid rgba(55, 65, 81, 0.5)',
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.25)' }
        }}
    >
        <Typography sx={{ fontSize: '13px', color: '#b4bcd0', fontFamily: 'monospace', fontWeight: 500 }}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography sx={{ fontSize: '13px', color: valueColor, fontWeight: 700, fontFamily: 'monospace' }}>
                {value}
            </Typography>
            {subValue && (
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>
                    {subValue}
                </Typography>
            )}
        </Box>
    </Box>
);

/**
 * Section header - Institutional Grade Styling
 */
const SectionHeader = ({ title, badge = null }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            backgroundColor: 'rgba(220, 38, 38, 0.12)',
            borderBottom: '1px solid rgba(220, 38, 38, 0.4)'
        }}
    >
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', letterSpacing: '0.8px' }}>
            {title}
        </Typography>
        {badge && (
            <Typography sx={{ fontSize: '11px', color: '#fbbf24', fontWeight: 700 }}>
                {badge}
            </Typography>
        )}
    </Box>
);

/**
 * ADX Range interpretation
 */
const getADXInterpretation = (value) => {
    if (!value && value !== 0) return { text: 'N/A', color: '#6b7280' };
    if (value >= 40) return { text: 'EXTREME TREND', color: '#a855f7' };
    if (value >= 25) return { text: 'STRONG TREND', color: '#22c55e' };
    if (value >= 20) return { text: 'GOOD TREND', color: '#4ade80' };
    if (value >= 15) return { text: 'WEAK TREND', color: '#fbbf24' };
    return { text: 'NO TREND', color: '#ef4444' };
};

/**
 * ADX Range color based on category
 */
const getADXRangeColor = (range) => {
    if (!range) return '#6b7280';
    const r = range.toUpperCase();
    if (r === 'EXTREME_TREND' || r === 'EXTREME') return '#a855f7';
    if (r === 'STRONG_TREND' || r === 'STRONG') return '#22c55e';
    if (r === 'GOOD_TREND' || r === 'GOOD') return '#4ade80';
    if (r === 'WEAK_TREND' || r === 'WEAK') return '#fbbf24';
    if (r === 'NO_TREND' || r === 'NONE' || r === 'RANGING') return '#ef4444';
    return '#6b7280';
};

/**
 * Progress bar for ADX visualization - Institutional Grade Styling
 */
const ADXProgressBar = ({ value }) => {
    const normalizedValue = Math.min(Math.max(value || 0, 0), 60); // Clamp between 0-60
    const percentage = (normalizedValue / 60) * 100;
    const interpretation = getADXInterpretation(value);

    return (
        <Box sx={{ width: '100%', padding: '10px 12px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', fontWeight: 500 }}>
                    0
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#fbbf24', fontFamily: 'monospace', fontWeight: 600 }}>
                    20
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#22c55e', fontFamily: 'monospace', fontWeight: 600 }}>
                    40
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#a855f7', fontFamily: 'monospace', fontWeight: 600 }}>
                    60
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                    height: 8,
                    borderRadius: 2,
                    backgroundColor: 'rgba(55, 65, 81, 0.5)',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: interpretation.color,
                        borderRadius: 2
                    }
                }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.75 }}>
                <Typography sx={{ fontSize: '12px', color: interpretation.color, fontWeight: 700 }}>
                    {interpretation.text}
                </Typography>
            </Box>
        </Box>
    );
};

/**
 * ADXTable Component
 */
const ADXTable = memo(({ data, currentState }) => {
    // Extract data with fallbacks
    const h4ADX = data?.h4?.value || currentState?.adx?.value;
    const h4Range = data?.h4?.range || currentState?.adx?.range;
    const atrValue = data?.atr?.value;
    const atrPercent = data?.atr?.percentOfPrice;

    const formatValue = (value, decimals = 2) => {
        if (!value && value !== 0) return '--';
        return typeof value === 'number' ? value.toFixed(decimals) : value;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* ADX Analysis */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="ADX - TREND STRENGTH" badge={h4Range?.replace('_', ' ')} />
                <TableRow
                    label="H4 ADX Value"
                    value={formatValue(h4ADX, 1)}
                    valueColor={getADXRangeColor(h4Range)}
                />
                <TableRow
                    label="Trend Quality"
                    value={h4Range?.replace('_', ' ') || '--'}
                    valueColor={getADXRangeColor(h4Range)}
                />

                {/* Visual Progress Bar */}
                <ADXProgressBar value={h4ADX} />

                {/* ADX Legend - Improved visibility */}
                <Box
                    sx={{
                        padding: '8px 12px',
                        backgroundColor: 'rgba(55, 65, 81, 0.25)',
                        borderTop: '1px solid rgba(55, 65, 81, 0.5)'
                    }}
                >
                    <Typography sx={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace', mb: 0.75, fontWeight: 500 }}>
                        ADX Ranges:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        <Typography sx={{ fontSize: '10px', color: '#ef4444', fontWeight: 500 }}>0-15: No Trend</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#fbbf24', fontWeight: 500 }}>15-20: Weak</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#4ade80', fontWeight: 500 }}>20-25: Good</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#22c55e', fontWeight: 500 }}>25-40: Strong</Typography>
                        <Typography sx={{ fontSize: '10px', color: '#a855f7', fontWeight: 500 }}>40+: Extreme</Typography>
                    </Box>
                </Box>
            </Box>

            {/* ATR Analysis */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="ATR - VOLATILITY" />
                <TableRow
                    label="ATR Value"
                    value={formatValue(atrValue, 2)}
                    valueColor="#06b6d4"
                />
                <TableRow
                    label="% of Price"
                    value={atrPercent ? `${formatValue(atrPercent, 2)}%` : '--'}
                    valueColor="#06b6d4"
                />

                {/* ATR Interpretation */}
                {atrPercent !== undefined && (
                    <Box sx={{ padding: '8px' }}>
                        <Typography sx={{ fontSize: '10px', color: '#9ca3af', textAlign: 'center' }}>
                            {atrPercent >= 1.0 ? '🔥 High Volatility' :
                             atrPercent >= 0.5 ? '📊 Normal Volatility' :
                             '😴 Low Volatility'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Trading Implications - Improved readability */}
            <Box
                sx={{
                    backgroundColor: '#1a1a1a',
                    border: '1px solid #374151',
                    borderRadius: '4px',
                    overflow: 'hidden'
                }}
            >
                <SectionHeader title="TRADING IMPLICATIONS" />
                <Box sx={{ padding: '10px 12px' }}>
                    {h4ADX >= 25 ? (
                        <Typography sx={{ fontSize: '12px', color: '#22c55e', lineHeight: 1.7 }}>
                            ✅ Trend-following strategies are favorable
                            <br />
                            ✅ Consider breakout entries
                            <br />
                            ✅ Wider stops may be needed
                        </Typography>
                    ) : h4ADX >= 20 ? (
                        <Typography sx={{ fontSize: '12px', color: '#fbbf24', lineHeight: 1.7 }}>
                            ⚠️ Moderate trend - proceed with caution
                            <br />
                            ⚠️ Wait for confirmation before entry
                            <br />
                            ⚠️ Consider smaller position size
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: '12px', color: '#ef4444', lineHeight: 1.7 }}>
                            ❌ Avoid trend-following strategies
                            <br />
                            ❌ Range-bound / choppy conditions
                            <br />
                            ❌ Consider mean-reversion strategies
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
});

ADXTable.displayName = 'ADXTable';

export default ADXTable;
